import { useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Drawer } from '@/workspace/ui';
import {
  CheckCircle2, XCircle, Clock, RefreshCw, Zap, FlaskConical,
  Activity, Timer, ChevronDown, ChevronUp, Filter, X,
  Download, CalendarDays, RotateCcw, Loader2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useTimezone } from '@/components/TimezoneContext';
import './automation-controls.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

// ── date range helpers ────────────────────────────────────────────────────────
const DATE_OPTIONS = [
  { value: 'all',    label: 'All time' },
  { value: 'today',  label: 'Today' },
  { value: '7d',     label: 'Last 7 days' },
  { value: '30d',    label: 'Last 30 days' },
  { value: '90d',    label: 'Last 90 days' },
  { value: 'custom', label: 'Custom range…' },
];

/**
 * Extract YYYY-MM-DD using LOCAL clock — NOT d.toISOString() which is UTC.
 * For UTC+ timezones, toISOString() would give the previous day.
 */
function localDateStr(d) {
  const y   = d.getFullYear();
  const m   = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function dateRangeToParams(range, customRange, todayString) {
  if (range === 'all') return {};
  if (range === 'custom' && !customRange?.from) return {};
  if (range === 'custom' && customRange?.from) {
    return {
      since: localDateStr(customRange.from),
      until: localDateStr(customRange.to || customRange.from),
    };
  }
  const today = todayString();   // YYYY-MM-DD in user's configured timezone
  if (range === 'today') return { since: today, until: today };
  const now  = new Date();
  const days = range === '7d' ? 7 : range === '30d' ? 30 : 90;
  const from = new Date(now); from.setDate(from.getDate() - days);
  return { since: localDateStr(from), until: today };
}

function fmtRangeLabel(customRange) {
  if (!customRange?.from) return 'Custom range…';
  // Use the browser's local timezone for display — same as the calendar renders.
  // Using the app's timezone context here would show the wrong day for UTC+ users
  // because the calendar creates Date objects at midnight local browser time.
  const opts  = { month: 'short', day: 'numeric', year: 'numeric' };
  const from  = customRange.from.toLocaleDateString('en-US', opts);
  if (!customRange.to || customRange.from.toDateString() === customRange.to.toDateString())
    return from;
  const to = customRange.to.toLocaleDateString('en-US', opts);
  return `${from} – ${to}`;
}

function timeAgo(ts, formatDateTime) {
  if (!ts) return '—';
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 5)     return 'just now';
  if (s < 60)    return `${s}s ago`;
  if (s < 3600)  return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return formatDateTime(ts);
}

// ── CSV export ────────────────────────────────────────────────────────────────
function exportCsv(runs, automationName) {
  const headers = [
    'Date', 'Action Name', 'Webhook URL',
    'Contact Email', 'Contact Name', 'Contact Phone', 'Run Type',
    'HTTP Status', 'Success', 'Duration (ms)', 'Contact ID', 'Payload',
  ];
  const rows = runs.map(r => [
    new Date(r.triggered_at).toISOString(),
    r.action_name    || '',
    r.webhook_url    || '',
    r.contact_email  || '',
    r.contact_name   || '',
    r.payload?.phone || '',
    r.run_type,
    r.http_status    ?? '',
    r.success ? 'Yes' : 'No',
    r.duration_ms    ?? '',
    r.contact_id     || '',
    r.payload ? JSON.stringify(r.payload) : '',
  ]);
  const csv = [headers, ...rows]
    .map(row => row.map(value => {
      const text = String(value ?? '');
      const safe = /^\s*[=+@-]/.test(text) ? `'${text}` : text;
      return `"${safe.replace(/"/g, '""')}"`;
    }).join(','))
    .join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${(automationName || 'automation').replace(/\s+/g, '-').toLowerCase()}-runs-${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ── sub-components ────────────────────────────────────────────────────────────
function StatusBadge({ success, httpStatus }) {
  if (httpStatus == null) return (
    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 rounded"
      style={{ height: 20, fontWeight: 500, backgroundColor: 'var(--surface-2)', color: 'var(--text-muted)', border: '1px solid var(--stroke)' }}>
      <Timer size={11} /> No response
    </span>
  );
  const ok = success || (httpStatus >= 200 && httpStatus < 300);
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 rounded"
      style={{
        height: 20,
        fontWeight: ok ? 600 : 500,
        backgroundColor: ok ? 'var(--lime-soft)' : 'var(--surface-2)',
        color:           ok ? 'var(--ink)' : 'var(--red-error)',
        border:          `1px solid ${ok ? 'var(--lime-soft)' : 'var(--stroke)'}`,
      }}>
      {ok ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
      HTTP {httpStatus}
    </span>
  );
}

function RunTypeBadge({ type }) {
  const isTest  = type === 'test';
  const isRetry = type === 'retry';
  const color   = isTest ? 'var(--brand-red)' : isRetry ? 'var(--text-muted)' : 'var(--ink)';
  const bg      = isTest ? 'var(--lime-tint)' : 'var(--surface-2)';
  const border  = isTest ? 'var(--lime-border)' : 'var(--stroke)';
  const icon    = isTest ? <FlaskConical size={10} /> : isRetry ? <RotateCcw size={10} /> : <Activity size={10} />;
  const label   = isTest ? 'Test' : isRetry ? 'Retry' : 'Live';
  return (
    <span className="inline-flex items-center gap-1 text-[11px] px-1.5 rounded"
      style={{ height: 20, fontWeight: 500, backgroundColor: bg, color, border: `1px solid ${border}` }}>
      {icon}{label}
    </span>
  );
}

function RunCard({ run, automationId, onRetried }) {
  const [open,     setOpen]     = useState(false);
  const [retrying, setRetrying] = useState(false);
  const retryingRef = useRef(false);
  const [confirmRetry, setConfirmRetry] = useState(false);
  const [retryError, setRetryError] = useState('');
  const retryButtonRef = useRef(null);
  const { formatDateTime, formatTime } = useTimezone();
  const ok = run.success || (run.http_status >= 200 && run.http_status < 300);

  // Derive a short, readable destination label for the collapsed row.
  // Priority: explicit action name → webhook URL hostname → nothing.
  const webhookLabel = (() => {
    if (run.action_name) return run.action_name;
    if (run.webhook_url) {
      try {
        const host = new URL(run.webhook_url).hostname.replace(/^www\./, '');
        // Keep just the first meaningful segment, e.g. "hooks.zapier.com" → "zapier.com"
        const parts = host.split('.');
        return parts.length > 2 ? parts.slice(-2).join('.') : host;
      } catch { return null; }
    }
    return null;
  })();

  const handleRetry = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (retryingRef.current) return;
    retryingRef.current = true;
    setRetrying(true);
    setRetryError('');
    try {
      const res  = await fetch(`${API}/automations/${automationId}/runs/${run.id}/retry`, { method: 'POST' });
      const data = await res.json();
      if (res.ok && data.success) {
        toast.success(`Retry succeeded → HTTP ${data.http_status} (${data.duration_ms}ms)`);
        setConfirmRetry(false);
      } else {
        const message = `Retry failed: ${data.error || (typeof data.detail === 'string' ? data.detail : null) || `HTTP ${data.http_status || res.status}`}`;
        setRetryError(message);
        toast.error(message);
      }
      if (res.ok && onRetried) await onRetried();
    } catch (err) {
      const message = 'Retry request failed. Check your connection and try again.';
      setRetryError(message);
      toast.error(message);
    } finally {
      retryingRef.current = false;
      setRetrying(false);
    }
  };
  let prettyResponse = run.response_body;
  try { if (run.response_body) prettyResponse = JSON.stringify(JSON.parse(run.response_body), null, 2); }
  catch { /* keep */ }

  return (
    <div
      className="rounded-sm border overflow-hidden transition-all"
      style={{ borderColor: 'var(--stroke)', backgroundColor: '#ffffff' }}>
      <AlertDialog open={confirmRetry} onOpenChange={value => { if (!retryingRef.current) setConfirmRetry(value); }}>
        <AlertDialogContent className="sp-dialog sp-automation-dialog" onCloseAutoFocus={event => { event.preventDefault(); retryButtonRef.current?.focus(); }}>
          <AlertDialogHeader>
            <AlertDialogTitle>Retry this run?</AlertDialogTitle>
            <AlertDialogDescription>
              Send this run’s original payload to {webhookLabel || 'its destination'} again. This is a real delivery and may repeat actions in the receiving service.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {retryError && <p role="alert" className="sp-automation-error">{retryError}</p>}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={retrying}>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={retrying} aria-busy={retrying} onClick={handleRetry}>
              {retrying ? 'Retrying…' : 'Retry run'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="sp-run-heading flex items-center gap-3 px-5 py-3">
        <button type="button" className="sp-run-disclosure flex items-center gap-3 min-w-0 flex-1 text-left py-2"
          aria-expanded={open} aria-controls={`run-details-${run.id}`}
          aria-label={`${open ? 'Hide' : 'Show'} run details for ${run.contact_name || run.contact_email || 'Anonymous / Test'}`}
          onClick={() => setOpen(value => !value)}>
          <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: ok ? 'var(--lime)' : 'var(--red-error)' }} />
          <span className="flex-1 min-w-0">
            <span className="flex items-center flex-wrap gap-2">
              <span className="text-[13px] font-medium" style={{ color: 'var(--ink)' }}>{run.contact_name || run.contact_email || 'Anonymous / Test'}</span>
              {run.contact_email && run.contact_name && <span className="text-xs" style={{ color: 'var(--text-dim)' }}>{run.contact_email}</span>}
            </span>
            <span className="flex items-center gap-2 mt-1 flex-wrap">
              <RunTypeBadge type={run.run_type} />
              <StatusBadge success={run.success} httpStatus={run.http_status} />
              {webhookLabel && <span className="text-[11px]" style={{ color: 'var(--text-muted)' }} title={run.webhook_url}>→ {webhookLabel}</span>}
              {run.duration_ms != null && <span className="text-xs" style={{ color: 'var(--text-dim)' }}>{run.duration_ms}ms</span>}
            </span>
            <span className="block text-xs mt-1" style={{ color: 'var(--text-dim)' }}>{timeAgo(run.triggered_at, formatDateTime)} · {formatTime(run.triggered_at)}</span>
          </span>
          {open ? <ChevronUp size={14} className="shrink-0" /> : <ChevronDown size={14} className="shrink-0" />}
        </button>
        {!ok && <button type="button"
          ref={retryButtonRef}
          onClick={() => { setRetryError(''); setConfirmRetry(true); }} disabled={retrying}
          className="sp-run-retry flex items-center gap-1.5 shrink-0 text-xs font-medium h-9 px-3 rounded-sm border bg-white"
          style={{ borderColor: 'var(--stroke)', color: 'var(--ink)' }} title="Retry this run">
          {retrying ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
          {retrying ? 'Retrying…' : 'Retry'}
        </button>}
      </div>

      {open && (
        <div id={`run-details-${run.id}`} className="border-t px-5 pb-5 pt-4 space-y-4" style={{ borderColor: 'var(--stroke-soft)' }}>
          {run.error && (
            <div className="rounded-md p-3" style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke)' }}>
              <p className="text-[11px] font-semibold mb-1" style={{ color: 'var(--red-error)' }}>Error</p>
              <p className="text-xs" style={{ color: 'var(--red-error)', fontFamily: 'var(--font-mono)' }}>{run.error}</p>
            </div>
          )}
          {!run.success && run.response_body && (() => {
            try {
              const p = JSON.parse(run.response_body);
              const hint = p.hint || p.message;
              if (!hint) return null;
              return (
                <div className="rounded-md p-3 flex items-start gap-2"
                  style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke)' }}>
                  <span className="text-[11px] font-semibold shrink-0" style={{ color: 'var(--ink)' }}>Hint:</span>
                  <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{hint}</p>
                </div>
              );
            } catch { return null; }
          })()}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <p className="text-[11px] font-semibold mb-2" style={{ color: '#20211f', opacity: 0.65 }}>Payload Sent</p>
              <pre className="text-xs rounded-sm p-3 overflow-auto max-h-56"
                style={{ backgroundColor: '#F3F6EC', border: '1px solid #D8E2C4', fontFamily: 'var(--font-mono)', color: '#20211f', lineHeight: 1.6 }}>
                {JSON.stringify(run.payload, null, 2)}
              </pre>
            </div>
            <div>
              <p className="text-[11px] font-semibold mb-2" style={{ color: 'var(--text-dim)' }}>Response Body</p>
              {prettyResponse ? (
                <pre className="text-xs rounded-md p-3 overflow-auto max-h-56"
                  style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke)', fontFamily: 'var(--font-mono)', color: ok ? 'var(--ink)' : 'var(--red-error)', lineHeight: 1.6 }}>
                  {prettyResponse}
                </pre>
              ) : ok ? (
                <div className="rounded-md p-3 text-xs" style={{ backgroundColor: 'var(--lime-tint)', border: '1px solid var(--lime-border)', color: 'var(--ink)' }}>
                  <p className="font-semibold mb-1">Webhook acknowledged ✓</p>
                  <p style={{ color: 'var(--text-muted)' }}>No response body — normal for n8n and most webhook services.</p>
                </div>
              ) : (
                <div className="rounded-md p-3 text-xs" style={{ backgroundColor: 'var(--surface-2)', border: '1px solid var(--stroke)', color: 'var(--text-dim)' }}>No response body</div>
              )}
            </div>
          </div>
          <div className="flex items-center flex-wrap gap-4 pt-1">
            <div><span className="text-xs" style={{ color: 'var(--text-dim)' }}>Run ID </span>
              <code className="text-xs font-mono" style={{ color: 'var(--brand-navy)', fontFamily: 'var(--font-mono)' }}>{run.id.substring(0, 12)}…</code>
            </div>
            {run.contact_id && (
              <div><span className="text-xs" style={{ color: 'var(--text-dim)' }}>Contact </span>
                <code className="text-xs font-mono" style={{ color: 'var(--brand-navy)', fontFamily: 'var(--font-mono)' }}>{run.contact_id.substring(0, 12)}…</code>
              </div>
            )}
            <div><span className="text-xs" style={{ color: 'var(--text-dim)' }}>Duration </span>
              <span className="text-xs font-semibold" style={{ color: 'var(--text-muted)' }}>
                {run.duration_ms != null ? `${run.duration_ms}ms` : '—'}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── main export ───────────────────────────────────────────────────────────────
export function AutomationRuns({ open, automation, onClose }) {
  const qc = useQueryClient();
  const { timezone, formatDate, formatDateTime, todayString } = useTimezone();
  const [dateRange,   setDateRange]   = useState('all');
  const [typeFilter,  setTypeFilter]  = useState('all');
  const [customRange, setCustomRange] = useState(null);
  const [calOpen,     setCalOpen]     = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const refreshingRef = useRef(false);

  const handleDateRangeChange = (val) => {
    setDateRange(val);
    if (val !== 'custom') setCustomRange(null);
    if (val === 'custom') setCalOpen(true);
  };

  const dateParams  = dateRangeToParams(dateRange, customRange, todayString);
  const queryParams = new URLSearchParams({ limit: '2000' });
  if (dateParams.since) queryParams.set('since', dateParams.since);
  if (dateParams.until) queryParams.set('until', dateParams.until);
  // Always send timezone so the backend converts dates to exact UTC bounds
  if (dateParams.since || dateParams.until) queryParams.set('tz', timezone);
  if (typeFilter !== 'all') queryParams.set('run_type', typeFilter);

  const queryKey = ['automation-runs', automation?.id, dateRange, typeFilter,
                    customRange?.from?.toISOString(), customRange?.to?.toISOString()];

  const { data: runs = [], isLoading, isFetching, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => fetch(`${API}/automations/${automation.id}/runs?${queryParams}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); }),
    enabled: open && !!automation?.id,
    refetchOnMount: true,
  });

  const handleRefresh = async () => {
    if (refreshingRef.current || isFetching) return;
    refreshingRef.current = true;
    setRefreshing(true);
    try { await refetch(); }
    finally { refreshingRef.current = false; setRefreshing(false); }
  };
  const activeFilters = (dateRange !== 'all' ? 1 : 0) + (typeFilter !== 'all' ? 1 : 0);


  const successCount = runs.filter(r => r.success).length;
  const failCount    = runs.filter(r => !r.success).length;
  const testCount    = runs.filter(r => r.run_type === 'test').length;
  const liveCount    = runs.filter(r => r.run_type === 'live').length;

  return (
    <Drawer open={open} onClose={onClose} label="Automation run history" eyebrow="Workflow activity" wide>
      <div className="sp-runs-panel">
            {/* ── Header ── */}
            <div className="px-6 pt-6 pb-5 border-b shrink-0"
              style={{ backgroundColor: 'var(--surface-2)', borderColor: 'var(--stroke)' }}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-sm flex items-center justify-center mt-0.5"
                    style={{ backgroundColor: 'rgba(32,33,31,0.12)' }}>
                    <Activity size={20} style={{ color: '#20211f' }} />
                  </div>
                  <div>
                    <h2 className="text-[15px] font-semibold"
                      style={{ fontFamily: 'var(--font)', color: 'var(--brand-navy)', letterSpacing: '-0.02em' }}>
                      Run History
                    </h2>
                    <p className="text-sm font-semibold mt-0.5" style={{ color: 'var(--brand-navy)', opacity: 0.55 }}>
                      {automation?.name}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-1">
                  <Button
                    variant="outline" size="sm"
                    onClick={() => exportCsv(runs, automation?.name)}
                    disabled={runs.length === 0}
                    className="gap-2 h-9 px-4 text-sm font-semibold"
                    style={{ borderColor: 'var(--stroke)', color: 'var(--ink)', backgroundColor: '#ffffff' }}
                  >
                    <Download size={14} />
                    Export CSV
                    {runs.length > 0 && (
                      <span className="text-[10px] font-semibold px-1 py-0.5 rounded ml-0.5"
                        style={{ backgroundColor: 'rgba(32,33,31,0.07)', color: 'var(--ink)' }}>
                        {runs.length}
                      </span>
                    )}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleRefresh}
                    disabled={isFetching || refreshing} aria-busy={isFetching || refreshing}
                    aria-label="Refresh run history" className="h-9 w-9 p-0 rounded-sm" style={{ color: 'var(--text-dim)' }}>
                    <RefreshCw size={14} className={isFetching || refreshing ? 'animate-spin' : undefined} />
                  </Button>

                </div>
              </div>

              {/* Summary stats */}
              {!isLoading && runs.length > 0 && (
                <div className="sp-runs-summary">
                  {[
                    { label: 'Total',   value: runs.length,  color: 'var(--ink)' },
                    { label: 'Live',    value: liveCount,    color: 'var(--ink)' },
                    { label: 'Test',    value: testCount,    color: 'var(--brand-red)' },
                    { label: 'Success', value: successCount, color: 'var(--ink)' },
                    { label: 'Failed',  value: failCount,    color: failCount > 0 ? 'var(--red-error)' : 'var(--text-dim)' },
                  ].map(s => (
                    <div key={s.label} className="text-center px-4 py-2 rounded-sm border" style={{ backgroundColor: '#ffffff', borderColor: 'var(--stroke)' }}>
                      <div className="text-[16px] font-semibold tabular-nums" style={{ color: s.color }}>{s.value}</div>
                      <div className="text-[11px]" style={{ color: 'var(--text-dim)' }}>{s.label}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── Filter bar ── */}
            <div className="flex items-center gap-3 px-6 py-4 border-b shrink-0 flex-wrap"
              style={{ borderColor: 'var(--stroke)', backgroundColor: '#fafaf8' }}>
              <div className="flex items-center gap-1.5 shrink-0" style={{ color: 'var(--text-dim)' }}>
                <Filter size={13} />
                <span className="text-xs font-semibold">Filter</span>
                {activeFilters > 0 && (
                  <span className="inline-flex items-center justify-center w-4 h-4 text-[10px] font-bold rounded"
                    style={{ backgroundColor: 'rgba(32,33,31,0.07)', color: 'var(--ink)' }}>
                    {activeFilters}
                  </span>
                )}
              </div>

              {/* Date range select */}
              <Select value={dateRange} onValueChange={handleDateRangeChange}>
                <SelectTrigger aria-label="Run date range" className="h-9 text-sm w-44" style={{ borderColor: 'var(--stroke)' }}>
                  <SelectValue>
                    {dateRange === 'custom'
                      ? fmtRangeLabel(customRange)
                      : DATE_OPTIONS.find(o => o.value === dateRange)?.label || 'All time'}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="sp-automation-select">
                  {DATE_OPTIONS.map(o => (
                    <SelectItem key={o.value} value={o.value} className="text-sm">{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Calendar popover for custom range */}
              <Popover modal open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <button
                    className="h-9 px-3 text-sm font-medium rounded-sm border flex items-center gap-1.5 transition-colors"
                    style={{ borderColor: dateRange === 'custom' && customRange ? 'var(--brand-navy)' : 'var(--stroke)',
                             color: dateRange === 'custom' && customRange ? 'var(--brand-navy)' : 'var(--text-dim)',
                             backgroundColor: '#ffffff' }}
                    onClick={() => setDateRange('custom')}
                  >
                    <CalendarDays size={14} />
                    {dateRange === 'custom' && customRange
                      ? fmtRangeLabel(customRange)
                      : 'Pick dates'}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="sp-automation-calendar w-auto p-0" align="start" aria-label="Choose run dates"
                  style={{ backgroundColor: '#ffffff', border: '1px solid var(--stroke)', borderRadius: '4px', boxShadow: 'var(--shadow)' }}>
                  <Calendar
                    mode="range"
                    selected={customRange}
                    onSelect={range => {
                      setCustomRange(range);
                      if (range?.from) {
                        setDateRange('custom');
                        if (range.to) setCalOpen(false);
                      }
                    }}
                    numberOfMonths={2}
                    initialFocus
                  />
                  <div className="px-4 pb-3 flex items-center justify-between">
                    <span className="text-xs" style={{ color: 'var(--text-dim)' }}>
                      {customRange?.from
                        ? `${customRange.from.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}${customRange.to ? ' → ' + customRange.to.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : ' → pick end'}`
                        : 'Select start date'}
                    </span>
                    {customRange && (
                      <button className="text-xs font-semibold" style={{ color: 'var(--brand-red)' }}
                        onClick={() => { setCustomRange(null); setDateRange('all'); setCalOpen(false); }}>
                        Clear
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Run type */}
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger aria-label="Run type" className="h-9 text-sm w-32" style={{ borderColor: 'var(--stroke)' }}>
                  <SelectValue placeholder="All types" />
                </SelectTrigger>
                <SelectContent className="sp-automation-select">
                  <SelectItem value="all"  className="text-sm">All types</SelectItem>
                  <SelectItem value="live" className="text-sm">Live only</SelectItem>
                  <SelectItem value="test" className="text-sm">Test only</SelectItem>
                </SelectContent>
              </Select>

              {activeFilters > 0 && (
                <button
                  onClick={() => { setDateRange('all'); setTypeFilter('all'); setCustomRange(null); }}
                  className="flex items-center gap-1 text-sm font-medium px-3 py-2 rounded-sm border transition-colors"
                  style={{ color: 'var(--text-muted)', borderColor: 'var(--stroke)' }}>
                  <X size={12} /> Clear
                </button>
              )}

              <div className="ml-auto flex items-center gap-3">
                {/* Timezone indicator */}
                <span className="text-xs font-medium hidden sm:flex items-center gap-1" style={{ color: 'var(--text-dim)' }}>
                  <Clock size={11} />
                  {timezone.split('/').pop().replace(/_/g, ' ')}
                </span>
                <span className="text-sm font-bold tabular-nums" style={{ color: 'var(--brand-navy)' }}>
                  {runs.length} run{runs.length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>

            {/* ── Runs list ── */}
            <div className="flex-1 overflow-y-auto px-6 py-6">
              {isError ? (
                <div className="sp-query-error" role="alert">
                  <p>Could not load run history. Your automation has not been changed.</p>
                  <button type="button" className="btn" disabled={isFetching || refreshing} onClick={handleRefresh}>
                    {isFetching || refreshing ? 'Loading…' : 'Try again'}
                  </button>
                </div>
              ) : isLoading ? (
                <div className="space-y-3 max-w-4xl mx-auto">
                  {[1,2,3,4,5,6].map(i => <Skeleton key={i} className="h-20 rounded-sm" style={{ backgroundColor: '#f0ede8' }} />)}
                </div>
              ) : runs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4">
                  <div className="w-16 h-16 rounded-sm flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #EAEFE0, #F3F6EC)' }}>
                    <Zap size={28} style={{ color: '#20211f' }} />
                  </div>
                  <div className="text-center">
                    <p className="text-base font-bold mb-1"
                      style={{ color: 'var(--brand-navy)', fontFamily: 'var(--font)' }}>
                      No runs {activeFilters > 0 ? 'match your filters' : 'yet'}
                    </p>
                    <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                      {activeFilters > 0
                        ? 'Try a wider date range or clear the filters.'
                        : 'Run history will appear here once this automation fires.'}
                    </p>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 max-w-4xl mx-auto">
                  {runs.map(run => (
                    <RunCard
                      key={run.id}
                      run={run}
                      automationId={automation?.id}
                      onRetried={handleRefresh}
                    />
                  ))}
                </div>
              )}
            </div>
      </div>
    </Drawer>
  );
}
